import { useCallback, useEffect, useRef, useState } from 'react'

type UseCountdown = {
  startTime: number
  intervalMs?: number
}

export function useCountdown({ startTime, intervalMs = 1000 }: UseCountdown) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const [countdown, setCountdown] = useState(0)

  const stopTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const tick = useCallback(() => {
    setCountdown((sec) => {
      const left = sec - 1
      if (left <= 0) {
        stopTimer()
        return 0
      }
      return left
    })
  }, [stopTimer])

  const startTimer = useCallback(() => {
    setCountdown(startTime)
    stopTimer()
    intervalRef.current = setInterval(tick, intervalMs)
  }, [startTime, intervalMs, tick])

  return { countdown, startTimer, stopTimer }
}

export function useAutomaticCountdown({ startTime, intervalMs = 1000 }: UseCountdown) {
  const { countdown, startTimer, stopTimer } = useCountdown({ startTime, intervalMs })

  useEffect(() => {
    startTimer()
    return () => {
      stopTimer()
    }
  }, [startTimer, stopTimer])

  return { countdown }
}
